import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { RefreshCw, X, Zap, ExternalLink } from "lucide-react";
import { BACKEND_URL } from "../../lib/backend";

const API = BACKEND_URL + "/api";
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—";

const STEP_NAMES = {
  generate_brief: "Brief",
  generate_newsletter: "Newsletter",
  generate_podcast_script: "Podcast",
};

export const AutopilotSelectionPanel = ({ dm, authHeader }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState({});
  const [confirmId, setConfirmId] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/admin/full-autopilot/selection`, { headers: authHeader() });
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load autopilot selection");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const removeArticle = async (articleId) => {
    setRemoving(r => ({ ...r, [articleId]: true }));
    setError("");
    try {
      const res = await axios.delete(
        `${API}/admin/full-autopilot/selection/${articleId}?run_id=${data.run_id}`,
        { headers: authHeader() }
      );
      setData(d => ({ ...d, articles: res.data.articles }));
    } catch (e) {
      setError(e.response?.data?.detail || "Remove failed");
    } finally {
      setRemoving(r => ({ ...r, [articleId]: false }));
      setConfirmId(null);
    }
  };

  const articles = data?.articles || [];
  const consumed = data?.consumed_by || [];
  const minRemaining = data?.min_remaining || 3;
  const atMinimum = articles.length <= minRemaining;
  const ROLE_CLS = {
    lead: dm ? "bg-cyan-400/20 text-cyan-200" : "bg-[#006874]/15 text-[#006874]",
    radar: dm ? "bg-white/8 text-slate-300" : "bg-[#e7e8e9] text-[#3b494c]",
    signal: dm ? "bg-amber-400/15 text-amber-300" : "bg-amber-50 text-amber-700",
  };
  const setRole = async (articleId, role) => {
    setError("");
    try {
      const res = await axios.post(`${API}/admin/full-autopilot/selection/set-role`, { run_id: data.run_id, article_id: articleId, role }, { headers: authHeader() });
      setData(d => ({ ...d, articles: res.data.articles }));
    } catch (e) {
      setError(e.response?.data?.detail || "Role change failed");
    }
  };

  return (
    <div
      data-testid="autopilot-selection-panel"
      className={`rounded-2xl border-2 p-6 ${dm ? "border-amber-400/25 bg-amber-400/5" : "border-amber-500/25 bg-amber-50/40"}`}
      style={{ boxShadow: "0 0 0 1px rgba(186,201,204,0.04), 0 2px 12px rgba(25,28,29,0.04)" }}
    >
      <div className="flex items-start justify-between mb-1.5">
        <div>
          <h3 className={`editorial-heading text-base font-medium flex items-center gap-2 ${dm ? "text-slate-200" : "text-[#191c1d]"}`}>
            <Zap className={`w-4 h-4 ${dm ? "text-amber-400" : "text-amber-600"}`} />
            Autopilot selection
            {data?.week_id && (
              <span className={`category-tag text-xs px-2 py-0.5 rounded-full ${dm ? "bg-amber-400/15 text-amber-400" : "bg-amber-500/10 text-amber-700"}`}>
                {data.week_id.split("-").slice(0, 2).join("-")}
              </span>
            )}
            {articles.length > 0 && (
              <span data-testid="autopilot-selection-count" className={`category-tag text-xs px-2 py-0.5 rounded-full ${dm ? "bg-white/6 text-slate-400" : "bg-[#f3f4f5] text-[#6b7a7d]"}`}>
                {articles.length} articles
              </span>
            )}
          </h3>
          <p className={`category-tag mt-0.5 text-xs ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
            Rank order — lead first, then radar, then signals — is the same in the API, MCP, the checkpoint email and Mission Control. Remove any article before the next step runs (min {minRemaining} must remain).
            {data?.curation?.model && <> · curated by <span data-testid="autopilot-selection-model">{data.curation.model}{data.curation.fallback ? ` (fallback: ${data.curation.fallback})` : ""}</span>{data.curation.directive_id ? " · MC directive applied" : ""}</>}
          </p>
          {consumed.length > 0 && (
            <p className={`category-tag mt-1 text-xs ${dm ? "text-amber-400" : "text-amber-700"}`}>
              Already used by: {consumed.map(s => STEP_NAMES[s] || s).join(", ")} — removal only affects the remaining channels.
            </p>
          )}
        </div>
        <button
          onClick={load}
          data-testid="autopilot-selection-refresh-btn"
          className={`p-1.5 rounded-lg transition-all ${dm ? "text-slate-500 hover:text-slate-300" : "text-[#bac9cc] hover:text-[#6b7a7d]"}`}
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {error && (
        <p data-testid="autopilot-selection-error" className="category-tag text-xs mt-2 text-red-500">{error}</p>
      )}

      {loading ? (
        <div className="flex justify-center py-6"><RefreshCw className={`w-4 h-4 animate-spin ${dm ? "text-amber-400" : "text-amber-600"}`} /></div>
      ) : !data?.run_id ? (
        <p data-testid="autopilot-selection-empty" className={`category-tag text-sm py-4 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
          No autopilot run this week yet — the selection will appear here after Step 1 (select news).
        </p>
      ) : articles.length === 0 ? (
        <p data-testid="autopilot-selection-empty" className={`category-tag text-sm py-4 ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
          Selection is empty — Step 1 (select news) hasn&apos;t completed yet.
        </p>
      ) : (
        <div className="mt-3 space-y-0.5">
          {articles.map((a, i) => (
            <div
              key={a.id}
              data-testid={`autopilot-selection-item-${a.id}`}
              className={`flex items-center gap-3 py-2 px-2 rounded-lg group ${dm ? "hover:bg-white/4" : "hover:bg-black/3"} border-b last:border-b-0 ${dm ? "border-white/5" : "border-black/5"}`}
            >
              <span className={`category-tag text-xs w-5 text-right flex-shrink-0 ${dm ? "text-slate-600" : "text-[#bac9cc]"}`} data-testid={`autopilot-selection-rank-${a.id}`}>{a.rank || i + 1}</span>
              <div className="min-w-0 flex-1">
                <p className={`category-tag text-xs flex items-center gap-1.5 flex-wrap ${dm ? "text-slate-500" : "text-[#6b7a7d]"}`}>
                  <span data-testid={`autopilot-selection-role-${a.id}`} className={`px-1.5 py-0.5 rounded-full text-[9px] ${ROLE_CLS[a.role] || ROLE_CLS.radar}`}>{(a.role || "radar").toUpperCase()}</span>
                  {(a.category || "?").toUpperCase()} · {a.source || "?"} · {fmtDate(a.publishedAt)}{a.origin && a.origin !== "auto" ? ` · ${a.origin}` : ""}{a.cluster_size ? ` · ${a.cluster_size} in cluster` : ""}
                </p>
                <a
                  href={a.originalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`text-sm font-medium leading-snug line-clamp-1 hover:underline flex items-center gap-1 ${dm ? "text-slate-200" : "text-[#191c1d]"}`}
                >
                  {a.title}
                  <ExternalLink className="w-3 h-3 opacity-0 group-hover:opacity-60 flex-shrink-0" />
                </a>
                {a.reason && <p className={`text-xs mt-0.5 line-clamp-1 ${dm ? "text-slate-400" : "text-[#3b494c]"}`} data-testid={`autopilot-selection-reason-${a.id}`}>{a.reason}</p>}
              </div>
              {a.role !== "lead" && confirmId !== a.id && (
                <button onClick={() => setRole(a.id, "lead")} data-testid={`autopilot-selection-make-lead-${a.id}`} title="Make this the lead story"
                  className={`category-tag text-[10px] px-2 py-1 rounded-full flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity ${dm ? "bg-cyan-400/15 text-cyan-200 hover:bg-cyan-400/25" : "bg-[#006874]/10 text-[#006874] hover:bg-[#006874]/20"}`}>
                  Make lead
                </button>
              )}
              {confirmId === a.id ? (
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => removeArticle(a.id)}
                    disabled={!!removing[a.id]}
                    data-testid={`autopilot-selection-confirm-remove-${a.id}`}
                    className="category-tag text-xs px-2.5 py-1 rounded-full bg-red-500 text-white hover:bg-red-600 disabled:opacity-50"
                  >
                    {removing[a.id] ? "Removing…" : "Remove?"}
                  </button>
                  <button
                    onClick={() => setConfirmId(null)}
                    data-testid={`autopilot-selection-cancel-remove-${a.id}`}
                    className={`category-tag text-xs px-2 py-1 rounded-full ${dm ? "text-slate-400 hover:bg-white/6" : "text-[#6b7a7d] hover:bg-black/5"}`}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setConfirmId(a.id)}
                  disabled={atMinimum}
                  data-testid={`autopilot-selection-remove-${a.id}`}
                  title={atMinimum ? `Minimum ${minRemaining} articles must remain` : "Remove from this week's selection"}
                  className={`p-1.5 rounded-lg flex-shrink-0 transition-all disabled:opacity-30 disabled:cursor-not-allowed ${dm ? "text-slate-500 hover:text-red-400 hover:bg-red-400/10" : "text-[#bac9cc] hover:text-red-500 hover:bg-red-50"}`}
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AutopilotSelectionPanel;
